import React, { useState, useEffect } from 'react';
import { multiFactor } from 'firebase/auth';
import { useAuth } from '../../contexts/AuthContext';
import MFASetup from './MFASetup';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { getSystemConfig, isMFARequiredForUser } from '../../services/systemConfigService';
import { SystemConfig } from '../../types/systemConfig';

interface MFAWrapperProps {
  children: React.ReactNode;
}

const MFAWrapper: React.FC<MFAWrapperProps> = ({ children }) => {
  const { user: authUser } = useAuth();
  const { t } = useLanguage();
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [hasMFA, setHasMFA] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [gracePeriodInfo, setGracePeriodInfo] = useState<{ daysRemaining?: number; gracePeriodEnds?: Date } | null>(null);

  useEffect(() => {
    const checkMFAStatus = async () => {
      if (!authUser) {
        setIsChecking(false);
        return;
      }

      try {
        // Import auth from firebase to get current user
        const { auth } = await import('../../lib/firebase');
        const currentUser = auth.currentUser;
        
        if (!currentUser) {
          setIsChecking(false);
          return;
        }

        // Skip MFA/verification checks on onboarding and pricing pages
        const isOnboarding = window.location.pathname === '/onboarding';
        const isPricing = window.location.pathname === '/pricing';
        if (isOnboarding || isPricing) {
          setIsChecking(false);
          return;
        }

        // Check email verification for email/password users
        const isEmailUser = currentUser.providerData.some(p => p.providerId === 'password');
        if (isEmailUser && !currentUser.emailVerified) {
          toast.error('📧 Email cím nincs megerősítve! Menj a Profilodba és kattints a "Megerősítő email küldése" gombra.', {
            duration: 10000,
            icon: '⚠️',
          });
        }

        // Check if user has MFA enrolled
        const enrolledFactors = multiFactor(currentUser).enrolledFactors;
        const hasMFAEnrolled = enrolledFactors.length > 0;
        
        setHasMFA(hasMFAEnrolled);

        // Load global MFA policy
        const config = await getSystemConfig();
        setSystemConfig(config);

        if (config && !hasMFAEnrolled) {
          // Check if MFA is required for this user
          const mfaCheck = isMFARequiredForUser(
            config.mfaPolicy,
            authUser.role || 'user',
            authUser.createdAt
          );

          setMfaRequired(mfaCheck.required);

          if (mfaCheck.required) {
            setShowMFASetup(true);
            
            if (mfaCheck.daysRemaining && mfaCheck.gracePeriodEnds) {
              setGracePeriodInfo({
                daysRemaining: mfaCheck.daysRemaining,
                gracePeriodEnds: mfaCheck.gracePeriodEnds
              });
              
              // Show warning toast about grace period
              toast(
                `⚠️ MFA kötelező! ${mfaCheck.daysRemaining} nap van hátra a bekapcsolásra.`,
                {
                  duration: 8000,
                  icon: '🔒',
                }
              );
            } else {
              // No grace period, MFA is immediately required
              toast.error('MFA kötelező! Kapcsold be most a folytatáshoz.', {
                duration: 10000,
                icon: '🔒',
              });
            }
          }
        }
      } catch (error: any) {
        console.error('Error checking MFA status:', error);
        
        // Handle quota exceeded error
        if (error.code === 'auth/quota-exceeded') {
          toast.error(
            'Túl sok próbálkozás! Kérlek várj 15-30 percet és próbáld újra.',
            {
              duration: 10000,
              icon: '⏳',
            }
          );
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkMFAStatus();
  }, [authUser]);

  const handleMFAComplete = () => {
    setShowMFASetup(false);
    setHasMFA(true);
    toast.success(t('mfa.setup.messages.success'), {
      duration: 5000,
      icon: '✅',
    });
    toast(t('mfa.setup.messages.successDescription'), {
      duration: 6000,
      icon: '🛡️',
    });
  };

  const handleMFACancel = () => {
    // Ha kötelező az MFA, ne engedjük bezárni
    if (mfaRequired) {
      if (gracePeriodInfo?.daysRemaining) {
        toast.error(
          `MFA kötelező! ${gracePeriodInfo.daysRemaining} nap van hátra a bekapcsolásra.`,
          {
            duration: 6000,
            icon: '🔒',
          }
        );
      } else {
        toast.error('MFA kötelező! Nem folytathatod a bekapcsolás nélkül.', {
          duration: 6000,
          icon: '🔒',
        });
      }
      return; // Ne zárjuk be a modal-t
    }
    
    // Ha opcionális, engedjük bezárni
    setShowMFASetup(false);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      
      {/* MFA Setup Modal - OPCIONÁLIS - csak akkor jelenik meg ha a felhasználó megnyitja */}
      {showMFASetup && !hasMFA && (
        <MFASetup
          onComplete={handleMFAComplete}
          onCancel={handleMFACancel}
        />
      )}
    </>
  );
};

export default MFAWrapper;
